/**
 * Read an SSE stream from a ReadableStreamDefaultReader, parse JSON data lines,
 * and call onEvent for each parsed event. Skips heartbeat events.
 *
 * Tracks the `event:` field and passes it as the second argument to onEvent.
 * Return `false` from onEvent to stop reading early (e.g. on an error event).
 * Always releases the reader lock when done.
 */
export async function readSSEStream<T>(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (data: T, eventType: string) => boolean | undefined,
  onHeartbeat?: () => void,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";
  let stopped = false;

  try {
    outer: while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim();
          if (currentEvent === "heartbeat") {
            onHeartbeat?.();
          }
        } else if (line.startsWith("data:")) {
          const jsonStr = line.slice(5).trim();
          if (!jsonStr || currentEvent === "heartbeat") {
            if (currentEvent === "heartbeat") {
              onHeartbeat?.();
            }
            currentEvent = "";
            continue;
          }
          let parsed: T;
          try {
            parsed = JSON.parse(jsonStr) as T;
          } catch {
            // Skip malformed JSON
            currentEvent = "";
            continue;
          }
          // Callback errors propagate to the caller
          if (onEvent(parsed, currentEvent) === false) {
            stopped = true;
            break outer;
          }
          currentEvent = "";
        }
      }
    }

    // Process remaining buffer if not stopped early
    if (!stopped && buffer.startsWith("data:")) {
      const jsonStr = buffer.slice(5).trim();
      if (jsonStr) {
        let parsed: T;
        try {
          parsed = JSON.parse(jsonStr) as T;
        } catch {
          return; // Skip malformed trailing data
        }
        onEvent(parsed, currentEvent);
      }
    }
  } finally {
    reader.releaseLock();
  }
}
